import { useState, useEffect, KeyboardEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import { useCreateLunchMenu } from '../../hooks/use-manage-lunch';
import { menuFormSchema, MenuFormValues } from '../../schemas/menu-form-schema';
import { WEEKDAYS } from '../../types/manage-lunch.types';

export interface MenuFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MenuFormDialog = ({ open, onOpenChange }: MenuFormDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: createLunchMenu, isPending } = useCreateLunchMenu();
  const [itemInput, setItemInput] = useState('');

  const form = useForm<MenuFormValues>({
    resolver: translatedZodResolver(menuFormSchema, t),
    defaultValues: { Name: '', WeekDay: WEEKDAYS[0], MenuItems: [] },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ Name: '', WeekDay: WEEKDAYS[0], MenuItems: [] });
    setItemInput('');
  }, [open, form]);

  const currentItems = form.watch('MenuItems') || [];

  const handleAddItem = (raw: string) => {
    const trimmed = raw.replace(/,/g, '').trim();
    if (!trimmed) return;
    if (!currentItems.includes(trimmed)) {
      const next = [...currentItems, trimmed];
      form.setValue('MenuItems', next, { shouldValidate: true });
    }
    setItemInput('');
  };

  const handleRemoveItem = (index: number) => {
    const next = currentItems.filter((_, i) => i !== index);
    form.setValue('MenuItems', next, { shouldValidate: true });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddItem(itemInput);
    } else if (e.key === 'Backspace' && !itemInput && currentItems.length > 0) {
      e.preventDefault();
      handleRemoveItem(currentItems.length - 1);
    }
  };

  const handleSubmit = async (values: MenuFormValues) => {
    // If user typed something and clicked Create without hitting Enter
    const finalItems = [...values.MenuItems];
    if (itemInput.trim()) {
      const trimmed = itemInput.replace(/,/g, '').trim();
      if (trimmed && !finalItems.includes(trimmed)) {
        finalItems.push(trimmed);
      }
    }

    try {
      await createLunchMenu({ ...values, MenuItems: finalItems, IsActive: true });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by mutation global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('ADD_MENU')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="WeekDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('WEEKDAY')}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('WEEKDAY')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WEEKDAYS.map((day) => (
                        <SelectItem key={day} value={day}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('MENU_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Menu 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="MenuItems"
              render={() => (
                <FormItem>
                  <FormLabel>{t('MENU_ITEMS')}</FormLabel>
                  <FormControl>
                    <div className="flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-card p-2 transition-colors focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                      {/* Numbered chips matching HR Module Prototype */}
                      {currentItems.map((item, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/60 py-0.5 pl-2 pr-1 text-xs text-foreground"
                        >
                          <span className="font-semibold text-muted-foreground/75 tabular-nums">
                            {idx + 1}
                          </span>
                          <span>{item}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(idx)}
                            aria-label={`Remove ${item}`}
                            className="rounded-xs p-0.5 text-muted-foreground hover:bg-neutral-200 hover:text-foreground dark:hover:bg-neutral-800"
                          >
                            <X className="size-3" />
                          </button>
                        </span>
                      ))}

                      <input
                        type="text"
                        value={itemInput}
                        onChange={(e) => setItemInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={() => handleAddItem(itemInput)}
                        placeholder={
                          currentItems.length === 0
                            ? 'Type item, press Enter or comma'
                            : 'Add item...'
                        }
                        className="min-w-28 flex-1 bg-transparent p-0.5 text-sm outline-none placeholder:text-muted-foreground"
                      />
                    </div>
                  </FormControl>
                  <p className="text-[11px] text-muted-foreground">
                    {t('MENU_ITEMS_HINT', {
                      defaultValue:
                        'Press Enter or comma to add each item. Items are numbered in order.',
                    })}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-2">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('CREATE')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
